import { readApiErrorMessage as readErrorMessage } from "src/api/errors";
import { authFetch } from "src/api/auth";
import { CreateUpdateInstructionArticleRequest, InstructionArticleDto } from "src/api/instructions";

export enum AppReportType {
  Bug = 1,
  Complaint = 2,
  Idea = 3,
  Abuse = 4,
  Other = 5,
}

export enum AppReportStatus {
  New = 1,
  Triage = 2,
  InProgress = 3,
  Resolved = 4,
  Rejected = 5,
}

export enum AppReportSeverity {
  Low = 1,
  Medium = 2,
  High = 3,
  Critical = 4,
}

export enum NotificationDeliveryStatus {
  Pending = 1,
  Sent = 2,
  Failed = 3,
  Skipped = 4,
  EndpointInactive = 5,
}

export interface AdminDashboardResponse {
  totalUsers: number;
  totalTeams: number;
  totalEvents: number;
  eventsLast7Days: number;
  activePushSubscriptions: number;
  inactivePushSubscriptions: number;
  totalNotifications: number;
  failedDeliveries: number;
  unreadReports: number;
  openReports: number;
  backendVersion: string;
  environment: string;
  emailConfigured: boolean;
  pushConfigured: boolean;
  imageKitConfigured: boolean;
}

export interface AdminUserDto {
  id: string;
  firstName: string;
  lastName: string;
  email?: string | null;
  emailConfirmed: boolean;
  role: number;
  appRole: number;
  phone?: string | null;
  jerseyNumber?: number | null;
  createdAt: string;
  teamsCount: number;
  pushSubscriptionsCount: number;
  teams: AdminUserTeamDto[];
}

export interface AdminUserTeamDto {
  teamId: string;
  teamName: string;
  role: number;
  badgeTitle?: string | null;
}

export interface AdminUserFilters {
  search?: string;
  appRole?: number | "";
  role?: number | "";
  emailConfirmed?: boolean | "";
  hasTeams?: boolean | "";
  hasPush?: boolean | "";
  sortBy?: string;
  sortDirection?: "asc" | "desc";
  page?: number;
  pageSize?: number;
}

export interface AdminUserListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: AdminUserDto[];
}

export interface AdminTeamDto {
  id: string;
  name: string;
  visibility: number;
  membersCount: number;
}

export interface AdminTeamListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: AdminTeamDto[];
}

export interface UpdateAdminUserRequest {
  firstName: string;
  lastName: string;
  email?: string | null;
  emailConfirmed: boolean;
  role: number;
  appRole: number;
  phone?: string | null;
  jerseyNumber?: number | null;
}

export interface AddAdminTeamMemberRequest {
  userId: string;
  role: number;
  badgeTitle?: string | null;
}

export interface UpdateAdminTeamMemberRequest {
  role: number;
  badgeTitle?: string | null;
}

export interface AppReportDto {
  id: string;
  userId?: string | null;
  userName?: string | null;
  userEmail?: string | null;
  type: AppReportType;
  status: AppReportStatus;
  severity: AppReportSeverity;
  title: string;
  message: string;
  route?: string | null;
  entityType?: string | null;
  entityId?: string | null;
  appVersion?: string | null;
  platform?: string | null;
  userAgent?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  resolvedAt?: string | null;
}

export interface AdminReportsListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: AppReportDto[];
}

export interface CreateAppReportRequest {
  type: AppReportType;
  severity?: AppReportSeverity;
  title: string;
  message: string;
  route?: string;
  entityType?: string;
  entityId?: string;
  appVersion?: string;
  platform?: string;
  userAgent?: string;
}

export interface ReleaseNoticeDto {
  id: string;
  version: string;
  title: string;
  body: string;
  isPublished: boolean;
  sendNotification: boolean;
  notificationSent: boolean;
  publishedAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  createdByUserId?: string | null;
}

export interface CreateUpdateReleaseNoticeRequest {
  version: string;
  title: string;
  body: string;
  sendNotification: boolean;
}

export interface NotificationDeliverySummaryResponse {
  total: number;
  sent: number;
  failed: number;
  skipped: number;
  endpointInactive: number;
  activePushSubscriptions: number;
  inactivePushSubscriptions: number;
}

export interface NotificationDeliveryDto {
  id: string;
  notificationId: string;
  userId: string;
  userName?: string | null;
  userEmail?: string | null;
  pushSubscriptionId?: string | null;
  status: NotificationDeliveryStatus;
  error?: string | null;
  endpointHash?: string | null;
  createdAt: string;
  sentAt?: string | null;
  notificationTitle?: string | null;
  notificationType?: number | null;
  notificationCategory?: number | null;
}

export interface NotificationDeliveryListResponse {
  page: number;
  pageSize: number;
  total: number;
  items: NotificationDeliveryDto[];
}

export interface DatabaseBackupDownload {
  blob: Blob;
  fileName: string;
}


const requireJson = async <T>(response: Response): Promise<T> => {
  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return (await response.json()) as T;
};

const getFileNameFromContentDisposition = (contentDisposition: string | null): string | null => {
  if (!contentDisposition) {
    return null;
  }

  const utf8Match = contentDisposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    return decodeURIComponent(utf8Match[1].replace(/"/g, ""));
  }

  const fileNameMatch = contentDisposition.match(/filename="?([^";]+)"?/i);
  return fileNameMatch?.[1] ?? null;
};

export async function getAdminDashboard(): Promise<AdminDashboardResponse> {
  return requireJson(await authFetch("/api/admin/dashboard"));
}

export async function getAdminUsers(filters: AdminUserFilters = {}): Promise<AdminUserListResponse> {
  const params = new URLSearchParams({
    page: String(filters.page ?? 1),
    pageSize: String(filters.pageSize ?? 25),
  });

  if (filters.search?.trim()) {
    params.set("search", filters.search.trim());
  }
  if (filters.appRole) params.set("appRole", String(filters.appRole));
  if (filters.role) params.set("role", String(filters.role));
  if (filters.emailConfirmed !== "" && filters.emailConfirmed !== undefined) params.set("emailConfirmed", String(filters.emailConfirmed));
  if (filters.hasTeams !== "" && filters.hasTeams !== undefined) params.set("hasTeams", String(filters.hasTeams));
  if (filters.hasPush !== "" && filters.hasPush !== undefined) params.set("hasPush", String(filters.hasPush));
  if (filters.sortBy) params.set("sortBy", filters.sortBy);
  if (filters.sortDirection) params.set("sortDirection", filters.sortDirection);

  return requireJson(await authFetch(`/api/admin/users?${params.toString()}`));
}

export async function updateAdminUser(id: string, request: UpdateAdminUserRequest): Promise<AdminUserDto> {
  return requireJson(await authFetch(`/api/admin/users/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function getAdminTeams(search = "", page = 1, pageSize = 100): Promise<AdminTeamListResponse> {
  const params = new URLSearchParams({ page: String(page), pageSize: String(pageSize) });
  if (search.trim()) {
    params.set("search", search.trim());
  }

  return requireJson(await authFetch(`/api/admin/teams?${params.toString()}`));
}

export async function addAdminTeamMember(teamId: string, request: AddAdminTeamMemberRequest): Promise<AdminUserDto> {
  return requireJson(await authFetch(`/api/admin/teams/${encodeURIComponent(teamId)}/members`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function removeAdminTeamMember(teamId: string, userId: string): Promise<void> {
  const response = await authFetch(`/api/admin/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, {
    method: "DELETE",
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }
}

export async function updateAdminTeamMember(teamId: string, userId: string, request: UpdateAdminTeamMemberRequest): Promise<AdminUserDto> {
  return requireJson(await authFetch(`/api/admin/teams/${encodeURIComponent(teamId)}/members/${encodeURIComponent(userId)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function getAdminReports(params: {
  status?: AppReportStatus | "";
  type?: AppReportType | "";
  severity?: AppReportSeverity | "";
  userId?: string;
  page?: number;
  pageSize?: number;
} = {}): Promise<AdminReportsListResponse> {
  const search = new URLSearchParams({
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? 25),
  });

  if (params.status) search.set("status", String(params.status));
  if (params.type) search.set("type", String(params.type));
  if (params.severity) search.set("severity", String(params.severity));
  if (params.userId) search.set("userId", params.userId);

  return requireJson(await authFetch(`/api/admin/reports?${search.toString()}`));
}

export async function getAdminReport(id: string): Promise<AppReportDto> {
  return requireJson(await authFetch(`/api/admin/reports/${encodeURIComponent(id)}`));
}

export async function updateAdminReportStatus(id: string, status: AppReportStatus): Promise<AppReportDto> {
  return requireJson(await authFetch(`/api/admin/reports/${encodeURIComponent(id)}/status`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ status }),
  }));
}

export async function sendAdminTestNotification(): Promise<void> {
  const response = await authFetch("/api/admin/notifications/test", { method: "POST" });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }
}

export async function getAdminInstructions(): Promise<InstructionArticleDto[]> {
  return requireJson(await authFetch("/api/admin/instructions"));
}

export async function createAdminInstruction(request: CreateUpdateInstructionArticleRequest): Promise<InstructionArticleDto> {
  return requireJson(await authFetch("/api/admin/instructions", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function updateAdminInstruction(id: string, request: CreateUpdateInstructionArticleRequest): Promise<InstructionArticleDto> {
  return requireJson(await authFetch(`/api/admin/instructions/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function deleteAdminInstruction(id: string): Promise<void> {
  const response = await authFetch(`/api/admin/instructions/${encodeURIComponent(id)}`, { method: "DELETE" });
  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }
}

export async function publishAdminInstruction(id: string): Promise<InstructionArticleDto> {
  return requireJson(await authFetch(`/api/admin/instructions/${encodeURIComponent(id)}/publish`, { method: "POST" }));
}

export async function unpublishAdminInstruction(id: string): Promise<InstructionArticleDto> {
  return requireJson(await authFetch(`/api/admin/instructions/${encodeURIComponent(id)}/unpublish`, { method: "POST" }));
}

export async function uploadAdminInstructionImage(file: File): Promise<string> {
  const formData = new FormData();
  formData.append("file", file);

  const response = await authFetch("/api/admin/instructions/upload-image", {
    method: "POST",
    body: formData,
  });

  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  const data = (await response.json()) as { imageUrl?: string };
  if (!data.imageUrl) {
    throw new Error("Сервер не вернул ссылку на изображение.");
  }

  return data.imageUrl;
}

export async function downloadDatabaseBackup(): Promise<DatabaseBackupDownload> {
  const response = await authFetch("/api/admin/backup/database");
  if (!response.ok) {
    throw new Error(await readErrorMessage(response));
  }

  return {
    blob: await response.blob(),
    fileName: getFileNameFromContentDisposition(response.headers.get("Content-Disposition")) ?? "hockeyplanner-backup.dump",
  };
}

export async function getAdminReleases(): Promise<ReleaseNoticeDto[]> {
  return requireJson(await authFetch("/api/admin/releases"));
}

export async function createAdminRelease(request: CreateUpdateReleaseNoticeRequest): Promise<ReleaseNoticeDto> {
  return requireJson(await authFetch("/api/admin/releases", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function updateAdminRelease(id: string, request: CreateUpdateReleaseNoticeRequest): Promise<ReleaseNoticeDto> {
  return requireJson(await authFetch(`/api/admin/releases/${encodeURIComponent(id)}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  }));
}

export async function publishAdminRelease(id: string): Promise<ReleaseNoticeDto> {
  return requireJson(await authFetch(`/api/admin/releases/${encodeURIComponent(id)}/publish`, { method: "POST" }));
}

export async function getNotificationDeliverySummary(params: {
  status?: NotificationDeliveryStatus | "";
} = {}): Promise<NotificationDeliverySummaryResponse> {
  const search = new URLSearchParams();
  if (params.status) search.set("status", String(params.status));

  const suffix = search.toString() ? `?${search.toString()}` : "";
  return requireJson(await authFetch(`/api/admin/notification-deliveries/summary${suffix}`));
}

export async function getNotificationDeliveries(params: {
  status?: NotificationDeliveryStatus | "";
  page?: number;
  pageSize?: number;
} = {}): Promise<NotificationDeliveryListResponse> {
  const search = new URLSearchParams({
    page: String(params.page ?? 1),
    pageSize: String(params.pageSize ?? 25),
  });

  if (params.status) search.set("status", String(params.status));

  return requireJson(await authFetch(`/api/admin/notification-deliveries?${search.toString()}`));
}

export async function createAppReport(request: CreateAppReportRequest): Promise<AppReportDto> {
  const response = await authFetch("/api/reports", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(request),
  });

  return requireJson(response);
}
